import { FaHeart, FaTrophy } from "react-icons/fa";

interface VivaClubProgress {
  letter: string;
  color: string;
  isCompleted: boolean;
  isCurrent: boolean;
}

interface GameHUDProps {
  score: number;
  lives: number;
  targetColor: string;
  targetLetter: string;
  gameState: 'waiting' | 'playing' | 'gameOver';
  onStartGame: () => void;
  onRestartGame: () => void;
  onBackToMenu: () => void;
  onShowLeaderboard: () => void;
  vivaClubProgress?: VivaClubProgress[];
  dailyPlaysRemaining?: number; // Number of plays remaining today
}

export const GameHUD = ({
  score,
  lives,
  targetColor,
  targetLetter,
  gameState,
  onStartGame,
  onRestartGame,
  onBackToMenu,
  onShowLeaderboard,
  vivaClubProgress,
  dailyPlaysRemaining = 1 // Default to 1 if not provided
}: GameHUDProps) => {
  if (gameState === 'waiting') {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-viva-black/80 pointer-events-auto z-50">
        <div className="text-center p-8 rounded-xl border border-viva-border bg-viva-surface/90 mx-4 max-w-md">
          <h1 className="text-4xl font-bold text-viva-purple mb-4">VivaClub Game</h1>
          <p className="text-viva-white mb-4 text-lg">
            Spell <span className="text-viva-purple font-bold">VIVACLUB</span> by tapping the correct tokens!
          </p>
          <div className="text-viva-gray mb-6 text-sm space-y-1">
            <p>• Target tokens must appear within 5 seconds</p>
            <p>• Missing a target token = Game Over</p>
            <p>• Faster taps = higher scores (up to 5 points!)</p>
            <p>• Wrong taps cost you a life</p>
          </div>
          <div className="flex flex-col gap-3 w-full">
            <button 
              onClick={onStartGame}
              className="w-full btn btn-primary font-bold px-6 py-3 pointer-events-auto"
              style={{ backgroundColor: 'hsl(var(--viva-purple))', borderColor: 'hsl(var(--viva-purple))' }}
            >
              Start Game
            </button>
            <button 
              onClick={onShowLeaderboard}
              className="w-full btn btn-outline px-6 py-3 pointer-events-auto flex items-center justify-center gap-2"
              style={{ borderColor: 'hsl(var(--viva-purple))', color: 'hsl(var(--viva-purple))' }}
            >
              <FaTrophy size={20} />
              View Leaderboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (gameState === 'gameOver') {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-viva-black/80 pointer-events-auto z-50">
        <div className="text-center p-8 rounded-xl border border-viva-border bg-viva-surface/90 mx-4 max-w-md">
          <h2 className="text-3xl font-bold text-red-500 mb-4">Game Over!</h2>
          <p className="text-viva-white text-xl mb-2">Final Score: {score}</p>
          <p className="text-viva-gray mb-8">
            {score > 10 ? "Excellent reflexes!" : score > 5 ? "Good job!" : "Keep practicing!"}
          </p>
          <div className="flex flex-col gap-3 w-full">
            {/* Only show Play Again if user has remaining plays */}
            {dailyPlaysRemaining > 0 ? (
              <button 
                onClick={onRestartGame}
                className="w-full btn font-bold px-6 py-3 bg-viva-purple hover:bg-viva-purple-light text-white border-none pointer-events-auto"
              >
                Play Again
              </button>
            ) : (
              <div className="w-full px-6 py-3 bg-red-500/20 border border-red-500 text-red-300 rounded-lg text-center">
                Daily Limit Reached
              </div>
            )}
            <button 
              onClick={onShowLeaderboard}
              className="w-full btn btn-outline px-6 py-3 pointer-events-auto flex items-center justify-center gap-2"
              style={{ borderColor: 'rgb(251, 191, 36)', color: 'rgb(251, 191, 36)' }}
            >
              <FaTrophy size={20} />
              View Leaderboard
            </button>
            <button 
              onClick={onBackToMenu}
              className="w-full btn btn-outline btn-ghost px-6 py-3 pointer-events-auto"
            >
              Back to Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Playing state - show game stats
  return (
    <div className="absolute top-0 left-0 right-0 p-4 z-10 pointer-events-none">
      <div className="flex flex-col gap-3">
        {/* Main HUD */}
        <div className="flex justify-between items-center bg-viva-surface/90 rounded-lg p-3 border border-viva-border">
          <div className="text-viva-white">
            <div className="text-sm text-viva-gray">Score</div>
            <div className="text-xl font-bold text-viva-purple">{score}</div>
          </div>
          
          <div className="flex items-center gap-1">
            {Array.from({ length: 3 }, (_, i) => (
              <FaHeart 
                key={i} 
                size={20} 
                className={i < lives ? "text-red-500" : "text-gray-600"} 
              />
            ))}
          </div>
          
          <div className="text-center">
            <div className="text-sm text-viva-gray">Target</div>
            <div className="flex items-center gap-1">
              <div 
                className="w-6 h-6 rounded-full border-2 border-viva-purple"
                style={{ backgroundColor: targetColor }}
              />
              <span className="text-xl font-bold text-viva-white">{targetLetter}</span>
            </div>
          </div>
          
          <button 
            onClick={onBackToMenu}
            className="btn btn-outline btn-sm pointer-events-auto"
          >
            Back
          </button>
        </div>
        
        {/* VIVACLUB Progress */}
        <div className="bg-viva-surface/90 rounded-lg p-3 border border-viva-border">
          <div className="text-sm text-viva-gray mb-2 text-center">VIVACLUB Progress</div>
          <div className="flex justify-center gap-2">
            {vivaClubProgress?.map((item, index) => (
              <div 
                key={index}
                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${
                  item.isCompleted 
                    ? 'border-green-500 text-viva-white' 
                    : item.isCurrent 
                      ? 'border-viva-white text-viva-white' 
                      : 'border-viva-gray text-viva-gray'
                }`}
                style={{ 
                  backgroundColor: item.isCompleted 
                    ? '#22c55e' 
                    : item.isCurrent 
                      ? item.color 
                      : 'transparent'
                }}
              >
                {item.letter}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
